import { useCallback, useMemo } from 'react';
import { TableHeaderVariant, TableWithHeader } from './shared/TableWithHeader';
import { DataTableRow } from '../../common/table/TableTypes';
import {
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { TableTypography } from '../../common/table/utils';
import { translateTypeToHumanName } from '../helpers';
import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { createStableLabelWrapper, markStyles } from '../dependencies/search/LabelWrapperComponent';
import { PossibleSectionInDependencies } from '../dependencies/search/types';

const columns = [
  { field: 'label', headerName: 'Label', width: '160px' },
  { field: 'parentType', headerName: 'Type', width: '100px' },
  { field: 'objectName', headerName: 'Object', width: '90px' },
];

interface RelatedObjectsTableItem {
  id: string;
  name: string;
  label: string;
  parentType: string;
  dependencyType: string;
  objectName: string;
}

const renderRows = ({
  items,
  ObjectNameLabelWrapper,
  section = 'dependencies',
}: {
  items: RelatedObjectsTableItem[];
  ObjectNameLabelWrapper: ReturnType<typeof createStableLabelWrapper>;
  section?: PossibleSectionInDependencies;
}) => {
  return items.map((row, index) => {
    return {
      id: row.id,
      name: row.name,
      label: (
        <TableTypography>
          <TableItemHighlightedText
            text={row.label}
            field="label"
            section={section}
            tableType="relatedObjects"
            rowIndex={index}
          />
        </TableTypography>
      ),
      parentType: (
        <TableTypography>
          <TableItemHighlightedText
            text={translateTypeToHumanName(row.parentType)}
            field="parentType"
            section={section}
            tableType="relatedObjects"
            rowIndex={index}
          />
        </TableTypography>
      ),
      objectName: row.objectName ? (
        <ObjectTypeLabelAndKeyTag
          objectType={row.objectName}
          LabelWrapperComponent={ObjectNameLabelWrapper}
          labelWrapperComponentSx={markStyles}
          labelWrapperComponentProps={{ rowIndex: index }}
        />
      ) : (
        <TableTypography>N/A</TableTypography>
      ),
      _parentType: row.parentType,
      dependencyType: row.dependencyType,
      isClickable: true,
    };
  });
};

interface RelatedObjectsTableProps {
  items: RelatedObjectsTableItem[];
  onItemClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  headerVariant?: TableHeaderVariant;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
}

export const RelatedObjectsTable = ({
  items,
  onItemClick,
  headerVariant,
  clearHistory,
  section = 'dependencies',
}: RelatedObjectsTableProps) => {
  // Create stable wrapper component ONCE per table, not per row
  const ObjectNameLabelWrapper = useMemo(
    () =>
      createStableLabelWrapper({
        field: 'objectName',
        section,
        tableType: 'relatedObjects',
      }),
    [section],
  );

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onItemClick({
        id: row.id,
        name: row._nameStr,
        parentType: row._parentType,
        dependencyType: row.dependencyType,
        clearHistory,
        contentType: DependenciesContentViewType.default,
      });
    },
    [onItemClick, clearHistory],
  );

  return (
    <TableWithHeader
      tableHeader="Related Objects"
      columns={columns}
      rows={renderRows({ items, ObjectNameLabelWrapper, section })}
      headerVariant={headerVariant}
      onRowClick={onRowClick}
      dataTableType="relatedObjects"
      section={section}
    />
  );
};
